// Le mouvement du produit : trois durées, une courbe, et la mémoire de ce qui
// a déjà été montré (chantier « Mouvement », issue #188).
//
// RÈGLES, pour tout ce qui bouge dans l'extension, Radar et la console :
// - l'image finale est l'état par défaut, l'animation part d'un état antérieur
//   et y revient ; sous « réduire les animations », rien ne bouge ;
// - une animation joue UNE fois : le score monte la première fois qu'on voit
//   une offre (décision de Nicolas du 2026-10-15), une liste joue une fois par
//   session ; revenir sur une page montre l'image finale, tout de suite ;
// - seuls transform, opacity et le tracé d'un SVG bougent.
//
// Les durées vivent aussi en CSS (variables '--m-*' de 'mouvementCss') : un
// composant qui anime en CSS lit la variable, un compteur en JS lit MOUVEMENT.

import { useEffect, useRef, useState } from 'react'

export const MOUVEMENT = {
  /** Réponse à un geste : un bouton, une pastille. */
  geste: 160,
  /** Une chose qui arrive : une carte, un bloc, une puce. */
  arrivee: 320,
  /** Un chiffre qui compte, un arc qui se remplit. */
  chiffre: 900,
  /** L'écart entre deux éléments d'une cascade (barres, cartes). */
  cascade: 90,
} as const

export const COURBE = 'cubic-bezier(.2,.8,.2,1)'

/** Les variables CSS des durées, à poser sur ':root' ou ':host'. */
export const mouvementCss = `
:root, :host {
  --m-geste: ${MOUVEMENT.geste}ms;
  --m-arrivee: ${MOUVEMENT.arrivee}ms;
  --m-chiffre: ${MOUVEMENT.chiffre}ms;
  --m-cascade: ${MOUVEMENT.cascade}ms;
  --m-courbe: ${COURBE};
}
`

/** Vrai si le visiteur a demandé de réduire les animations, ou si on ne peut pas le savoir. */
export function mouvementReduit(): boolean {
  try {
    return typeof matchMedia !== 'function' || matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return true
  }
}

/** Le minimum d'un stockage clé-valeur synchrone (localStorage, sessionStorage, un faux en test). */
export interface StockageSimple {
  getItem(cle: string): string | null
  setItem(cle: string, valeur: string): void
}

/** Combien d'identifiants une mémoire garde : au-delà, les plus anciens sortent. */
export const MAX_VUS = 400

/**
 * Une mémoire de ce qui a déjà été montré, rangée sous `cle` dans `stockage`.
 * `premiereFois(id)` répond vrai une seule fois par identifiant et le note.
 * Un stockage illisible (navigation privée, quota) répond « déjà vu » : mieux
 * vaut ne pas animer que rejouer à chaque fois.
 */
export function memoireDesVus(stockage: StockageSimple | null | undefined, cle: string) {
  const lire = (): string[] => {
    try {
      const brut = stockage?.getItem(cle)
      const liste = brut ? JSON.parse(brut) : []
      return Array.isArray(liste) ? liste.filter((x): x is string => typeof x === 'string') : []
    } catch {
      return []
    }
  }
  return {
    dejaVu(id: string): boolean {
      return lire().includes(id)
    },
    premiereFois(id: string): boolean {
      if (!stockage) return false
      const vus = lire()
      if (vus.includes(id)) return false
      try {
        stockage.setItem(cle, JSON.stringify([...vus, id].slice(-MAX_VUS)))
      } catch {
        return false
      }
      return true
    },
  }
}

/** Le stockage du navigateur s'il est lisible, sinon null (jamais une exception). */
export function stockageNavigateur(type: 'local' | 'session'): StockageSimple | null {
  try {
    const s = type === 'local' ? globalThis.localStorage : globalThis.sessionStorage
    return s ?? null
  } catch {
    return null
  }
}

/**
 * Un nombre qui compte de 0 à `valeur` en `MOUVEMENT.chiffre` ms quand `actif`,
 * avec la même courbe que l'arc. Inactif, ou mouvement réduit : la valeur tout
 * de suite. Une valeur qui change ensuite s'affiche directement.
 */
export function useCompteur(valeur: number, actif: boolean): number {
  // Décidé une fois, au montage : seule la première valeur compte, une valeur
  // qui change ensuite (score recalculé) s'affiche directement.
  const [joue] = useState(() => actif && !mouvementReduit() && typeof requestAnimationFrame === 'function')
  const [affiche, setAffiche] = useState(() => (joue ? 0 : valeur))
  const premier = useRef(true)
  useEffect(() => {
    const animer = joue && premier.current
    premier.current = false
    if (!animer) {
      setAffiche(valeur)
      return
    }
    const debut = performance.now()
    let image = 0
    const pas = (maintenant: number) => {
      const x = Math.min(1, (maintenant - debut) / MOUVEMENT.chiffre)
      // Même allure que la courbe CSS : rapide au départ, posée à l'arrivée.
      const e = 1 - Math.pow(1 - x, 3)
      setAffiche(Math.round(valeur * e))
      if (x < 1) image = requestAnimationFrame(pas)
    }
    image = requestAnimationFrame(pas)
    return () => cancelAnimationFrame(image)
    // `actif` ne change qu'au montage : la première valeur seule compte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valeur])
  return affiche
}
